import { create } from 'zustand';
import { doGET, doPUT } from '../../utils/HttpUtils';
import { showError, showSuccess } from '../toaster';

export type PlatformVersionConfig = {
  softUpdateVersion: string;
  hardUpdateVersion: string;
  storeUrl: string;
};

export type AppVersionSettings = {
  android: PlatformVersionConfig;
  ios: PlatformVersionConfig;
};

type AppVersionStore = {
  data: AppVersionSettings;
  isLoading: boolean;
  isSubmitting: boolean;
  fetchSettings: () => Promise<void>;
  saveSettings: (payload: AppVersionSettings) => Promise<boolean>;
};

const defaultData: AppVersionSettings = {
  android: {
    softUpdateVersion: '',
    hardUpdateVersion: '',
    storeUrl: '',
  },
  ios: {
    softUpdateVersion: '',
    hardUpdateVersion: '',
    storeUrl: '',
  },
};

const store = create<AppVersionStore>((set) => ({
  data: defaultData,
  isLoading: false,
  isSubmitting: false,

  fetchSettings: async () => {
    try {
      set({ isLoading: true });
      const response = await doGET('/app-versions');

      if (!response.error && response.status >= 200 && response.status < 400) {
        set({ data: response.data?.data || defaultData });
        return;
      }

      showError(response.message || 'Failed to fetch app version settings');
    } catch (_err) {
      showError('Failed to fetch app version settings');
    } finally {
      set({ isLoading: false });
    }
  },

  saveSettings: async (payload: AppVersionSettings) => {
    try {
      set({ isSubmitting: true });
      const response = await doPUT('/app-versions', payload);

      if (!response.error && response.status >= 200 && response.status < 400) {
        set({ data: response.data?.data || payload });
        showSuccess(response.data?.message || 'App version settings updated successfully');
        return true;
      }

      showError(response.message || 'Failed to update app version settings');
      return false;
    } catch (_err) {
      showError('Failed to update app version settings');
      return false;
    } finally {
      set({ isSubmitting: false });
    }
  },
}));

export const useAppVersionsStore = () => store((state) => state);
